import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { ExploreGraphSummary } from '@mindweb/shared'
import { api } from '@/lib/api'
import { Navbar } from '@/components/ui/navbar'
import { Seo } from '@/components/seo/seo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

interface ExplorePayload {
  readonly items: readonly ExploreGraphSummary[]
  readonly page: number
  readonly hasMore: boolean
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value)
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])
  return debounced
}

export function ExplorePage() {
  const [search, setSearch] = useState('')
  const [items, setItems] = useState<readonly ExploreGraphSummary[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { t } = useTranslation()

  const debouncedSearch = useDebounce(search, 300)

  // Reset to first page whenever the search term changes.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const result = await api.get<ExplorePayload>(
          `/api/explore?q=${encodeURIComponent(debouncedSearch)}&page=1`,
        )
        if (cancelled) return
        if (result.success && result.data) {
          setItems(result.data.items)
          setPage(1)
          setHasMore(result.data.hasMore)
        } else {
          setError(result.error ?? t('explore.empty'))
        }
      } catch {
        setError(t('share.failedToLoad'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [debouncedSearch])

  const handleLoadMore = async () => {
    setLoadingMore(true)
    try {
      const nextPage = page + 1
      const result = await api.get<ExplorePayload>(
        `/api/explore?q=${encodeURIComponent(debouncedSearch)}&page=${nextPage}`,
      )
      if (result.success && result.data) {
        setItems((prev) => [...prev, ...result.data!.items])
        setPage(nextPage)
        setHasMore(result.data.hasMore)
      }
    } catch {
      setError(t('share.failedToLoad'))
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <Seo title={t('seo.explore.title')} description={t('seo.explore.description')} path="/explore" />
      <Navbar />

      <main className="mx-auto max-w-5xl px-6 py-12">
        <section className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {t('explore.title')}
          </h1>
          <p className="mt-3 text-base text-muted-foreground">
            {t('explore.subtitle')}
          </p>
        </section>

        <div className="mb-10 mx-auto max-w-xl">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('explore.searchPlaceholder')}
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-32 rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <p className="text-center text-muted-foreground">{error}</p>
        ) : items.length === 0 ? (
          <p className="text-center text-muted-foreground">{t('explore.empty')}</p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((graph) => (
                <div
                  key={graph.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/share/${graph.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/share/${graph.id}`) } }}
                  className="landing-card flex h-full cursor-pointer flex-col rounded-lg border bg-card p-4 text-left transition-colors hover:bg-accent/50"
                >
                  <h3 className="font-medium text-foreground line-clamp-2">{graph.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{graph.topic}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Badge variant="secondary" className="text-[10px]">
                      {t('explore.childNodes', { count: graph.child_count })}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {t('explore.questionNodes', { count: graph.question_count })}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="mt-8 flex justify-center">
                <Button variant="secondary" onClick={handleLoadMore} loading={loadingMore}>
                  {t('explore.loadMore')}
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
